// import liteClient from 'algoliasearch/lite';
import algoliasearch from 'algoliasearch';
import type { SearchResponse } from '@algolia/client-search';

import { watch, useContext } from '@nuxtjs/composition-api';
import { AlgoliaMappedProduct, SearchState, UseAlgoliaSearchReturn, Banner } from './interfaces';
import { sharedRef } from '@vue-storefront/core';
import { PLP_PRODUCTS_PER_PAGE } from '@vue-storefront/novulo-theme/helpers/constants';
import { useSortingOptions } from '@vue-storefront/novulo-theme/composables/useSortingOptions';

type Hit = Record<string, any>;
type DeviceCtx = 'mobile' | 'desktop';

function summariseProduct(h: Hit) {
  return {
    id: Number(h.objectID),
    sku: h.productSKU,
    price: h.priceAfterDiscount ?? h.salesPriceInclVAT,
    inStock: h.inStockBool,
    stockCode: h.stockClassificationCode,
    reviewScore: h.reviewScore
  };
}

function summariseBanner(b: any) {
  return {
    title: b.image?.title,
    image: b.image?.urls?.[0]?.url,
    link: b.link?.url
  };
}

export function useAlgoliaSearch(
  translate: (...args: any[]) => string,
  extraFacetAttributes: string[] = []
): UseAlgoliaSearchReturn {
  function encodeInStockFacets(facets: Record<string, any>): Record<string, any> {
    if (!facets.inStockBool) {
      return facets;
    }

    const original = facets.inStockBool as Record<'true' | 'false', number>;
    const mapped: Record<string, number> = {};

    for (const [val, count] of Object.entries(original)) {
      const translated = translate(`algolia_facets_boolean_${val}`);
      mapped[translated] = count;
    }

    return { ...facets, inStockBool: mapped };
  }

  function decodeInStockFacetFilters(facetFilters: string[][]): string[][] {
    const yesTerm = translate('algolia_facets_boolean_true');
    const noTerm = translate('algolia_facets_boolean_false');

    return facetFilters.map(group =>
      group.map(filter => {
        const [attr, val] = filter.split(':');
        if (attr === 'inStockBool') {
          if (val === yesTerm) return 'inStockBool:true';
          if (val === noTerm) return 'inStockBool:false';
        }
        return filter;
      })
    );
  }

  const { $ddRum, $config, $logAlgolia } = useContext() as any;

  const { indexName } = useSortingOptions();
  const appId = $config.algoliaAppId;
  const searchKey = $config.algoliaSearchKey;

  const defaultHitsPerPage = PLP_PRODUCTS_PER_PAGE;
  const bannerHitsPerPage = PLP_PRODUCTS_PER_PAGE - 1;

  const searchState = sharedRef<SearchState>(
    {
      query: '',
      page: 0,
      hitsPerPage: defaultHitsPerPage,
      facetFilters: [],
      numericFilters: [],
      filters: '',
      indexName,
      ruleContexts: []
    },
    'useAlgoliaSearch-searchState'
  );

  const products = sharedRef<AlgoliaMappedProduct[]>([], 'useAlgoliaSearch-products');
  const totalCount = sharedRef<number>(0, 'useAlgoliaSearch-totalCount');
  const nbPages = sharedRef<number>(0, 'useAlgoliaSearch-nbPages');
  const facets = sharedRef<Record<string, any>>({}, 'useAlgoliaSearch-facets');
  const facetsStats = sharedRef<Record<string, any>>({}, 'useAlgoliaSearch-facetsStats');
  const banners = sharedRef<Banner[]>([], 'useAlgoliaSearch-banners');
  const headerBanners = sharedRef<Banner[]>([], 'useAlgoliaSearch-headerBanners');
  const isLoading = sharedRef<boolean>(false, 'useAlgoliaSearch-loading');
  const searchQueryID = sharedRef<string>('', 'useAlgoliaSearch-searchQueryID');
  const error = sharedRef<Error | null>(null, 'useAlgoliaSearch-error');

  const client = algoliasearch(appId, searchKey);

  const deviceRuleContexts = sharedRef<DeviceCtx[]>([], 'useAlgoliaSearch-deviceRuleContexts');

  function setDeviceRuleContext(device: 'mobile' | 'desktop') {
    deviceRuleContexts.value = [device];
  }

  const search = async () => {
    isLoading.value = true;
    error.value = null;

    try {
      const {
        query,
        page,
        facetFilters: rawFacetFilters,
        hitsPerPage: currentHitsPerPage,
        indexName,
        numericFilters,
        filters
      } = searchState.value;

      // decode our custom inStockBool facet keys back to Algolia's expected syntax
      const decodedFacetFilters = decodeInStockFacetFilters(rawFacetFilters);

      // handle banners-first load logic
      if (
        banners.value.length > 0 && 
        currentHitsPerPage === defaultHitsPerPage
      ) {
        searchState.value = {
          ...searchState.value,
          hitsPerPage: bannerHitsPerPage };

        return;
      }

      const hitsPerPage = searchState.value.hitsPerPage;

      const mergedRuleContexts = Array.from(
        new Set([...(searchState.value.ruleContexts || []), ...deviceRuleContexts.value])
      );

      if (JSON.stringify(searchState.value.ruleContexts || []) !== JSON.stringify(mergedRuleContexts)) {
        searchState.value = {
          ...searchState.value,
          ruleContexts: mergedRuleContexts
        };
      }

      // build the main query
      const mainQuery = {
        indexName,
        params: {
          facetFilters: decodedFacetFilters,
          facets: [...extraFacetAttributes, '*'],
          filters: filters ? `${filters} AND segment:true` : 'segment:true',
          hitsPerPage,
          numericFilters,
          page,
          query,
          ruleContexts: searchState.value.ruleContexts,
          clickAnalytics: true
        }
      };

      // build one query per disjunctive facet group
      const disjunctiveAttrs = Array.from(new Set(
        decodedFacetFilters.map(group => group[0].split(':')[0])
      ));

      const facetQueries = disjunctiveAttrs.map(attr => ({
        indexName,
        params: {
          facetFilters: decodedFacetFilters.filter(group => !group.some(f => f.startsWith(attr + ':'))),
          facets: attr,
          filters: filters ? `${filters} AND segment:true` : 'segment:true',
          hitsPerPage: 0,
          maxValuesPerFacet: 100,
          numericFilters,
          query,
          ruleContexts: searchState.value.ruleContexts
        }
      }));
      let raw: any;

      // FIRE!
      try {
        raw = await client.search<Record<string, any>>(
              [mainQuery, ...facetQueries] as any,
              { strategy: 'none' }
        );

        const main = raw.results[0] as any;
        const hits = main.hits as Hit[];
        const bannersRaw = main.renderingContent?.widgets?.banners ?? [];
        const dataDogPayload = {
          index: mainQuery.indexName,
          params: mainQuery.params,
          page: searchState.value.page,
          hitsPerPage: searchState.value.hitsPerPage,
          nbHits: main?.nbHits,
          processingTime: main?.processingTimeMS,
          queryID: main?.queryID,
          banners: bannersRaw.map(summariseBanner),
          products: hits.map(summariseProduct)
        };

        if (process.server) {
          $logAlgolia('algolia-search', dataDogPayload, null);
        } else {
          $ddRum?.addAction('algolia-search', dataDogPayload);
        }
      } catch (err) {
        if (process.server) {
          $logAlgolia('algolia-search', undefined, err as Error);
        } else {
          $ddRum?.addError(err as Error);
        }

        throw err;
      }

      const results = raw.results as SearchResponse<Record<string, any>>[];

      const queryID = results?.find(r => r?.hitsPerPage > 0)?.queryID || '';

      // merge back counts
      const main = results[0] as any;
      const allBanners: Banner[] = (main.renderingContent?.widgets?.banners || []).map(summariseBanner);
      const split = allBanners.reduce(
        (acc, b) => {
          if ((b.title || '').toLowerCase() === 'header') acc.header.push(b);
          else acc.content.push(b);

          return acc;
        },
        { header: [] as Banner[], content: [] as Banner[] }
      );
      headerBanners.value = split.header;
      banners.value = split.content;

      if (banners.value.length > 0 && currentHitsPerPage === defaultHitsPerPage) {
        main.hits.pop();
      }

      // map hits into our shape
      products.value = main.hits.map(mapHit);
      totalCount.value = main.nbHits;
      nbPages.value = main.nbPages;
      facetsStats.value = main.facets_stats;
      searchQueryID.value = queryID;

      // rebuild merged facets object
      const newFacets: Record<string, Record<string, number>> = { ...main.facets };
      disjunctiveAttrs.forEach((attr, i) => {
        newFacets[attr] = results[i + 1]?.facets?.[attr] || {};
      });

      // **encode** our inStockBool values for the UI
      facets.value = encodeInStockFacets(newFacets);
    } catch (e) {
      error.value = e as Error;
    } finally {
      isLoading.value = false;
    }
  };

  function mapStockCodeToRef(code: string): 0 | 1 {
    switch (code) {
      case 'in_stock':
      case 'digital_delivery':
        return 0;
      default:
        return 1;
    }
  }

  function mapHit(hit: any): AlgoliaMappedProduct {
    return {
      id: parseInt(hit.objectID, 10),
      name: hit.name,
      sku: hit.productSKU,
      product_id: hit.productID,
      spli_guid: null,
      spli_id: hit.spliID,
      brand: hit.brand?.name,
      brand_logo: null,
      media: hit.primaryImage || null,
      media_file_name: hit.primaryImage?.split('/').pop() || null,
      price: hit.priceAfterDiscount,
      manufacturers_suggested_retail_price: hit.recommendedRetailPrice,
      price_after_cash_discount: hit.priceAfterDiscount,
      in_stock: hit.inStockBool,
      total_reviews: hit.reviewCount,
      price_after_cashback_discount: hit.priceAfterCashback,
      rating_number: hit.reviewScore,
      first_promotion: hit.first_promotion,
      url: `/${hit.productURL}`,
      category: hit.primaryTag?.name || null,
      category_id: hit.primaryTag?.id || null,
      status: hit.stockClassification || hit.deliveryCondition || null,
      status_reference: mapStockCodeToRef(hit.stockClassificationCode),
      can_be_ordered: hit.canBeOrdered,
      is_occasion: false
    };
  }

  watch(searchState, search, { deep: true });

  return {
    banners,
    error,
    facets,
    facetsStats,
    headerBanners,
    isLoading,
    nbPages,
    products,
    search,
    searchQueryID,
    searchState,
    setDeviceRuleContext,
    totalCount
  };
}
